import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../../../firebase.init";
import {
  Server,
  Plus,
  Edit2,
  Trash2,
  Power,
  Send,
  ShieldCheck,
  Mail,
  AlertCircle,
  CheckCircle2,
  Lock,
  Save,
  ArrowLeft,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";

const API_BASE = "https://server.enjoywiki.com/email-server"; // Replace with your actual API base URL

const CustomSMTPSettings = () => {
  const [user] = useAuthState(auth);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);

  const [formData, setFormData] = useState({
    smtpHost: "",
    smtpPort: "",
    smtpUsername: "",
    smtpPassword: "",
    senderName: "",
    senderEmail: "",
    encryptionType: "TLS",
    enabled: true,
  });

  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user?.email) {
      fetchConfigs();
    }
  }, [user]);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE}/api/custom-smtp?ownerEmail=${user.email}`,
      );
      setConfigs(res.data);
    } catch (error) {
      console.error("Failed to fetch SMTP configs:", error);
    } finally {
      setLoading(false);
    }
  };

  const showStatus = (type, message) => {
    setStatusMessage({ type, message });
    setTimeout(() => setStatusMessage(null), 5000);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const openAddModal = () => {
    setEditingConfig(null);
    setFormData({
      smtpHost: "",
      smtpPort: "",
      smtpUsername: "",
      smtpPassword: "",
      senderName: "",
      senderEmail: "",
      encryptionType: "TLS",
      enabled: true,
    });
    setShowModal(true);
  };

  const openEditModal = (config) => {
    setEditingConfig(config);
    setFormData({
      ...config,
      smtpPassword: "********", // Masked placeholder
    });
    setShowModal(true);
  };

  const handleToggleEnable = async (id, currentStatus) => {
    try {
      await axios.put(`${API_BASE}/api/custom-smtp/${id}`, {
        enabled: !currentStatus,
      });
      fetchConfigs();
      showStatus(
        "success",
        `SMTP Configuration ${!currentStatus ? "Enabled" : "Disabled"}`,
      );
    } catch (error) {
      showStatus("error", "Failed to toggle status");
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this SMTP configuration?",
      )
    )
      return;
    try {
      await axios.delete(`${API_BASE}/api/custom-smtp/${id}`);
      fetchConfigs();
      showStatus("success", "SMTP Configuration Deleted");
    } catch (error) {
      showStatus("error", "Failed to delete configuration");
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    try {
      const payload = {
        ...formData,
        ownerEmail: user.email,
        _id: editingConfig ? editingConfig._id : undefined,
      };

      const res = await axios.post(`${API_BASE}/api/test-custom-smtp`, payload);
      toast.success(res.data.message || "Test email sent successfully!");
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
          "SMTP Connection Failed. Please check credentials.",
      );
    } finally {
      setIsTesting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payload = { ...formData, ownerEmail: user.email };

      if (editingConfig) {
        await axios.put(
          `${API_BASE}/api/custom-smtp/${editingConfig._id}`,
          payload,
        );
        showStatus("success", "Configuration updated securely.");
      } else {
        await axios.post(`${API_BASE}/api/custom-smtp`, payload);
        showStatus("success", "Configuration added securely.");
      }
      setShowModal(false);
      fetchConfigs();
    } catch (error) {
      showStatus("error", "Failed to save configuration.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="container-fluid mb-5 mt-3"
      style={{ maxWidth: "1400px" }}
    >
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div className="d-flex align-items-center gap-4">
          <Link
            to="/admin/email-marketing"
            className="btn btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center"
            style={{
              width: "40px",
              height: "40px",
              borderColor: "rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.03)",
            }}
            title="Back to Email Marketing"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h4
              className="fw-bold text-white mb-1 d-flex align-items-center"
              style={{ letterSpacing: "-0.5px" }}
            >
              <Server className="me-3 text-info" size={28} strokeWidth={2.5} />
              Custom SMTP Providers
            </h4>
            <p
              className="text-gray-400 mb-0 ms-5 d-none d-md-block"
              style={{ fontSize: "0.9rem" }}
            >
              Manage your multiple secure 3rd-party email delivery systems.
            </p>
          </div>
        </div>
        {configs.length === 0 && (
          <button
            className="btn premium-btn grad-blue d-flex align-items-center shadow-sm"
            onClick={openAddModal}
          >
            <Plus size={18} className="me-2" /> Add SMTP Provider
          </button>
        )}
      </div>

      <AnimatePresence>
        {statusMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`d-flex align-items-center p-3 mb-4 rounded-3 ${statusMessage.type === "success" ? "bg-success bg-opacity-10 border border-success border-opacity-25" : "bg-danger bg-opacity-10 border border-danger border-opacity-25"}`}
          >
            {statusMessage.type === "success" ? (
              <CheckCircle2
                className="text-success me-3 flex-shrink-0"
                size={20}
              />
            ) : (
              <AlertCircle
                className="text-danger me-3 flex-shrink-0"
                size={20}
              />
            )}
            <span
              className={
                statusMessage.type === "success"
                  ? "text-success"
                  : "text-danger"
              }
              style={{ fontSize: "0.9rem" }}
            >
              {statusMessage.message}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="row g-4">
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center py-5 mb-5 w-100"
            style={{ minHeight: "300px" }}
          >
            <div
              className="spinner-border text-info"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="visually-hidden">Loading Settings...</span>
            </div>
          </div>
        ) : configs.length === 0 ? (
          <div className="col-12 text-center py-5">
            <div
              className="premium-card p-5 mx-auto"
              style={{ maxWidth: "600px" }}
            >
              <div
                className="card-glow-effect grad-blue"
                style={{
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
              <div className="position-relative z-1">
                <Mail size={48} className="text-info opacity-50 mb-3 mx-auto" />
                <h5 className="fw-bold text-white mb-3">
                  No Custom SMTP Providers Found
                </h5>
                <p className="text-gray-400 mb-4">
                  Connect your own SMTP servers (Gmail, Amazon SES, SendGrid,
                  Mailgun, etc.) to start sending secure, reliable emails.
                </p>
                <button
                  className="btn premium-btn grad-blue d-inline-flex align-items-center shadow-sm"
                  onClick={openAddModal}
                >
                  <Plus size={18} className="me-2" /> Add Your First Provider
                </button>
              </div>
            </div>
          </div>
        ) : (
          configs.map((config, i) => (
            <div className="col-12 col-md-6 col-lg-4" key={config._id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="premium-card h-100 d-flex flex-column"
              >
                <div
                  className={`card-glow-effect ${config.enabled ? "grad-blue" : "bg-secondary opacity-10"}`}
                  style={{
                    top: "-50px",
                    right: "-50px",
                    left: "auto",
                    bottom: "auto",
                  }}
                />

                <div className="p-4 border-bottom border-gray-800 d-flex justify-content-between align-items-center position-relative z-1">
                  <div className="d-flex align-items-center">
                    <div
                      className={`icon-glow-box me-3 ${config.enabled ? "grad-blue" : "bg-dark border border-gray-700"}`}
                      style={{ width: "40px", height: "40px" }}
                    >
                      <ShieldCheck
                        size={20}
                        className={
                          config.enabled ? "text-white" : "text-gray-500"
                        }
                        strokeWidth={2.5}
                      />
                    </div>
                    <div>
                      <h6
                        className="fw-bold mb-0 text-white tracking-wider text-uppercase small text-truncate"
                        style={{ maxWidth: "150px" }}
                      >
                        {config.senderName || config.smtpHost}
                      </h6>
                      <span
                        className={
                          config.enabled ? "text-info" : "text-gray-500"
                        }
                        style={{ fontSize: "0.75rem" }}
                      >
                        {config.enabled ? "Active Provider" : "Disabled"}
                      </span>
                    </div>
                  </div>

                  <div className="form-check form-switch mb-0 p-0 d-flex align-items-center justify-content-end">
                    <input
                      className="form-check-input custom-switch m-0"
                      type="checkbox"
                      checked={config.enabled}
                      onChange={() =>
                        handleToggleEnable(config._id, config.enabled)
                      }
                      title={
                        config.enabled
                          ? "Disable this provider"
                          : "Enable this provider"
                      }
                      style={{
                        cursor: "pointer",
                        width: "40px",
                        height: "20px",
                      }}
                    />
                  </div>
                </div>

                <div className="p-4 flex-grow-1 position-relative z-1">
                  <div className="mb-4">
                    <small
                      className="text-gray-500 text-uppercase fw-bold d-block mb-1"
                      style={{ fontSize: "10px", letterSpacing: "1px" }}
                    >
                      Host Details
                    </small>
                    <div className="text-gray-300 fw-medium d-flex align-items-center">
                      <Server size={14} className="me-2 text-info opacity-75" />
                      {config.smtpHost} : {config.smtpPort}
                    </div>
                  </div>

                  <div className="mb-4">
                    <small
                      className="text-gray-500 text-uppercase fw-bold d-block mb-1"
                      style={{ fontSize: "10px", letterSpacing: "1px" }}
                    >
                      Authentication
                    </small>
                    <div className="text-gray-300 fw-medium text-truncate mb-1">
                      {config.smtpUsername}
                    </div>
                    <div
                      className="text-success small d-flex align-items-center"
                      style={{ fontSize: "0.8rem" }}
                    >
                      <Lock size={12} className="me-1" /> Password Encrypted
                    </div>
                  </div>

                  <div className="mb-0">
                    <small
                      className="text-gray-500 text-uppercase fw-bold d-block mb-1"
                      style={{ fontSize: "10px", letterSpacing: "1px" }}
                    >
                      Sender Settings
                    </small>
                    <div className="text-gray-300 fw-medium text-truncate mb-2">
                      {config.senderEmail}
                    </div>
                    <span className="badge bg-dark border border-gray-700 text-info px-2 py-1">
                      {config.encryptionType} Encryption
                    </span>
                  </div>
                </div>

                <div className="p-3 border-top border-gray-800 d-flex justify-content-end gap-2 position-relative z-1 bg-black bg-opacity-25 rounded-bottom-4">
                  <button
                    className="btn btn-sm btn-dark border-gray-700 text-gray-300 d-flex align-items-center custom-hover"
                    onClick={() => openEditModal(config)}
                  >
                    <Edit2 size={14} className="me-2" /> Edit
                  </button>
                  <button
                    className="btn btn-sm btn-dark border-gray-700 text-danger d-flex align-items-center custom-hover-danger"
                    onClick={() => handleDelete(config._id)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </motion.div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal (Dark Theme Premium) */}
      {showModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{
            backgroundColor: "rgba(0,0,0,0.7)",
            backdropFilter: "blur(8px)",
            zIndex: 1050,
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content premium-card border-0 shadow-lg">
              <div
                className="card-glow-effect grad-blue"
                style={{ top: "-100px", right: "-100px" }}
              />

              <div className="modal-header border-bottom border-gray-800 pt-4 pb-3 px-4 position-relative z-1">
                <div className="d-flex align-items-center">
                  <div
                    className="icon-glow-box grad-blue me-3"
                    style={{ width: "40px", height: "40px" }}
                  >
                    {editingConfig ? (
                      <Edit2 size={18} className="text-white" />
                    ) : (
                      <Plus size={18} className="text-white" />
                    )}
                  </div>
                  <h5 className="modal-title fw-bold text-white tracking-wider">
                    {editingConfig
                      ? "EDIT SMTP CONFIGURATION"
                      : "ADD SMTP PROVIDER"}
                  </h5>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>

              <div className="modal-body p-4 position-relative z-1">
                <form id="smtpForm" onSubmit={handleSubmit}>
                  <div className="row g-4">
                    <div className="col-md-8">
                      <label className="form-label text-gray-400 small fw-semibold tracking-wider text-uppercase">
                        SMTP Host *
                      </label>
                      <input
                        type="text"
                        className="form-control premium-input"
                        name="smtpHost"
                        value={formData.smtpHost}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g. smtp.gmail.com"
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label text-gray-400 small fw-semibold tracking-wider text-uppercase">
                        SMTP Port *
                      </label>
                      <input
                        type="number"
                        className="form-control premium-input"
                        name="smtpPort"
                        value={formData.smtpPort}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g. 587 or 465"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label text-gray-400 small fw-semibold tracking-wider text-uppercase">
                        SMTP Username *
                      </label>
                      <input
                        type="text"
                        className="form-control premium-input"
                        name="smtpUsername"
                        value={formData.smtpUsername}
                        onChange={handleInputChange}
                        required
                        placeholder="email@yourdomain.com"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label text-gray-400 small fw-semibold tracking-wider text-uppercase">
                        SMTP Password *
                      </label>
                      <input
                        type="password"
                        className="form-control premium-input"
                        name="smtpPassword"
                        value={formData.smtpPassword}
                        onChange={handleInputChange}
                        required
                        placeholder={
                          editingConfig
                            ? "Leave as ******** to keep current"
                            : "App Password or SMTP Password"
                        }
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label text-gray-400 small fw-semibold tracking-wider text-uppercase">
                        Sender Name *
                      </label>
                      <input
                        type="text"
                        className="form-control premium-input"
                        name="senderName"
                        value={formData.senderName}
                        onChange={handleInputChange}
                        required
                        placeholder="Your Organization Name"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label text-gray-400 small fw-semibold tracking-wider text-uppercase">
                        Sender Email *
                      </label>
                      <input
                        type="email"
                        className="form-control premium-input"
                        name="senderEmail"
                        value={formData.senderEmail}
                        onChange={handleInputChange}
                        required
                        placeholder="no-reply@yourdomain.com"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label text-gray-400 small fw-semibold tracking-wider text-uppercase">
                        Encryption Type *
                      </label>
                      <select
                        className="form-select premium-input"
                        name="encryptionType"
                        value={formData.encryptionType}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="TLS">TLS (Recommended)</option>
                        <option value="SSL">SSL</option>
                        <option value="None">None</option>
                      </select>
                    </div>

                    <div className="col-md-6 d-flex align-items-end pb-2">
                      <div
                        className="form-check form-switch p-3 rounded-3 w-100 d-flex align-items-center m-0 ps-4"
                        style={{
                          background: "rgba(255,255,255,0.03)",
                          border: "1px solid rgba(255,255,255,0.05)",
                        }}
                      >
                        <input
                          className="form-check-input custom-switch me-3 mt-0 ms-0"
                          type="checkbox"
                          id="enabledCheck"
                          name="enabled"
                          checked={formData.enabled}
                          onChange={handleInputChange}
                          style={{
                            width: "40px",
                            height: "20px",
                            cursor: "pointer",
                          }}
                        />
                        <label
                          className="form-check-label text-gray-300 fw-medium pt-1"
                          htmlFor="enabledCheck"
                          style={{ cursor: "pointer" }}
                        >
                          Enable immediately
                        </label>
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              <div className="modal-footer border-top border-gray-800 px-4 py-3 bg-black bg-opacity-25 rounded-bottom-4 position-relative z-1 d-flex justify-content-between">
                <button
                  type="button"
                  className="btn btn-dark border-gray-700 text-info d-flex align-items-center custom-hover"
                  onClick={handleTestConnection}
                  disabled={isTesting}
                >
                  {isTesting ? (
                    <span className="spinner-border spinner-border-sm me-2"></span>
                  ) : (
                    <Send size={16} className="me-2" />
                  )}
                  Test Connection
                </button>
                <div className="d-flex gap-3">
                  <button
                    type="button"
                    className="btn btn-dark border-gray-700 text-gray-300 custom-hover"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="smtpForm"
                    className="btn premium-btn grad-blue d-flex align-items-center"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <span className="spinner-border spinner-border-sm me-2"></span>
                    ) : (
                      <Save size={16} className="me-2" />
                    )}
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx="true">{`
        /* Premium Dark Theme Styles extracted from EmailMarketingSMTPForm */
        .premium-card {
          background: #111827;
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 1.25rem;
          box-shadow:
            0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
          position: relative;
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .premium-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            145deg,
            rgba(255, 255, 255, 0.03) 0%,
            rgba(255, 255, 255, 0) 100%
          );
          pointer-events: none;
          z-index: 0;
        }

        .icon-glow-box {
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.3);
        }

        .card-glow-effect {
          position: absolute;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.15;
          z-index: 0;
          pointer-events: none;
        }

        /* Inputs */
        .premium-input {
          background-color: rgba(0, 0, 0, 0.2) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          color: #f3f4f6 !important;
          border-radius: 0.5rem;
          padding: 0.75rem 1rem;
          transition: all 0.2s ease;
        }
        .premium-input:focus {
          background-color: rgba(0, 0, 0, 0.3) !important;
          border-color: rgba(56, 189, 248, 0.5) !important;
          box-shadow: 0 0 0 4px rgba(56, 189, 248, 0.1) !important;
        }
        .premium-input::placeholder {
          color: #6b7280;
        }

        /* Select fixing for dark mode */
        select.premium-input {
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e") !important;
        }
        select.premium-input option {
          background-color: #1f2937;
          color: #f3f4f6;
        }

        /* Buttons & Hovers */
        .premium-btn {
          color: white !important;
          border: none;
          padding: 0.6rem 1.5rem;
          border-radius: 0.5rem;
          font-weight: 500;
          letter-spacing: 0.025em;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
        }
        .premium-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
        }

        .custom-hover {
          transition: all 0.2s;
        }
        .custom-hover:hover {
          background-color: rgba(255, 255, 255, 0.05) !important;
          color: white !important;
        }

        .custom-hover-danger {
          transition: all 0.2s;
        }
        .custom-hover-danger:hover {
          background-color: rgba(220, 38, 38, 0.1) !important;
          border-color: rgba(220, 38, 38, 0.3) !important;
        }

        /* Toggle Switches */
        .custom-switch:checked {
          background-color: #3b82f6;
          border-color: #3b82f6;
        }

        /* Gradients */
        .grad-blue {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        }

        /* Typography & Colors */
        .text-gray-300 {
          color: #d1d5db;
        }
        .text-gray-400 {
          color: #9ca3af;
        }
        .text-gray-500 {
          color: #6b7280;
        }
        .border-gray-700 {
          border-color: rgba(255, 255, 255, 0.1) !important;
        }
        .border-gray-800 {
          border-color: rgba(255, 255, 255, 0.05) !important;
        }
        .tracking-wider {
          letter-spacing: 0.05em;
        }

        /* Modal tweaks */
        .btn-close-white {
          filter: invert(1) grayscale(100%) brightness(200%);
        }
      `}</style>
    </motion.div>
  );
};

export default CustomSMTPSettings;
